import { useState } from 'react'
import { Download, Eye, Paperclip, Upload } from 'lucide-react'
import { uploadDocument, useProject } from '../app/data'
import { fetchDocument } from '../infrastructure/api'
import { ArtifactViewer, Modal } from './ui'
import { ActionForm, Empty } from './forms'

export type DocumentBinding = {
  equipmentId?: string
  operatorId?: string
  workOrderId?: string
  requestId?: string
}
export function Attachments({ binding, readOnly = false }: { binding: DocumentBinding; readOnly?: boolean }) {
  const data = useProject()
  const [selected, setSelected] = useState('')
  const [upload, setUpload] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState('')
  const rows = data.artifacts.filter((doc) =>
    Object.entries(binding).every(([key, value]) => doc[key as keyof DocumentBinding] === value),
  )
  const target = rows.find((doc) => doc.id === selected)
  return (
    <section>
      <div className="section-heading">
        <h3>
          <Paperclip size={18} />
          Документы <span>{rows.length}</span>
        </h3>
        {!readOnly && (
          <button className="button primary" onClick={() => setUpload(true)}>
            <Upload size={16} />
            Загрузить
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="alert">
          {error}
        </p>
      )}
      {!rows.length && <Empty>Документы пока не прикреплены</Empty>}
      <div className="attachment-list">
        {rows.map((doc) => (
          <article key={doc.id}>
            <div className="file-extension">{doc.extension}</div>
            <div>
              <strong>{doc.name}</strong>
              <small>
                {doc.category} · {doc.generated ? doc.generationStatus === 'ready' ? doc.size : doc.generationStatus === 'error' ? 'Ошибка PDF' : 'PDF формируется' : doc.size}
              </small>
            </div>
            <button
              className="icon-button"
              title="Предпросмотр"
              aria-label={'Предпросмотр ' + doc.name}
              onClick={() => setSelected(doc.id)}
            >
              <Eye size={18} />
            </button>
            <button
              className="icon-button"
              disabled={doc.generated && doc.generationStatus !== 'ready'}
              aria-label={'Скачать ' + doc.name}
              title="Скачать"
              onClick={async () => {
                setError('')
                try {
                  const blob = await fetchDocument(doc.id)
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = doc.name
                  a.click()
                  window.setTimeout(() => URL.revokeObjectURL(url), 5000)
                } catch (e) {
                  setError(e instanceof Error ? e.message : 'Не удалось скачать')
                }
              }}
            >
              <Download size={18} />
            </button>
          </article>
        ))}
      </div>
      {target && (
        <Modal title="Предпросмотр документа" onClose={() => setSelected('')}>
          <ArtifactViewer artifact={target} />
        </Modal>
      )}
      {upload && (
        <Modal title="Прикрепить документ" onClose={() => setUpload(false)}>
          <ActionForm
            initial={{ category: 'Документы' }}
            fields={[{ name: 'category', label: 'Категория документа' }]}
            submit={async (v) => {
              if (!file) throw new Error('Выберите файл')
              await uploadDocument(file, String(v.category), binding.requestId ?? '', binding)
            }}
            onDone={() => {
              setUpload(false)
              setFile(null)
            }}
            label="Загрузить документ"
          >
            <label className="upload-zone">
              <Upload size={24} />
              <span>{file?.name ?? 'PDF, DOCX, XLSX, DOC, TXT или изображение · до 8 МБ'}</span>
              <input
                type="file"
                aria-label="Файл документа"
                accept=".pdf,.doc,.docx,.xlsx,.txt,.png,.jpg,.jpeg"
                required
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </ActionForm>
        </Modal>
      )}
    </section>
  )
}
